import { useState } from "react";
import { useSetPageHeader } from "@/lib/page-header";
import { useIsMobile } from "@/lib/use-media-query";
import { useGruposVisiveis } from "@/components/layout/use-grupos-visiveis";
import { useOrdemDoMenu } from "@/components/layout/use-ordem-do-menu";
import { MenuReordenavel } from "@/components/layout/MenuReordenavel";
import { SidebarRodape } from "@/components/layout/SidebarRodape";
import { CartaoComDestaque } from "@/components/shared/CartaoComDestaque";
import { VEIO_DO_MENU } from "@/components/layout/VoltarAoMenu";

/**
 * Os mesmos destinos do menu lateral, em cartões. Sai da mesma lista e dos mesmos filtros (tipo de
 * organização, permissão, plano) e na ordem que a pessoa arrastou — a página nunca mostra o que o
 * menu esconde, nem o contrário. Sem os títulos de seção: aqui é uma grade só.
 *
 * No celular não há menu lateral, então o "Reorganizar menu" mora aqui embaixo.
 */
export function Menu() {
  useSetPageHeader({ title: "Menu", subtitle: "Todas as áreas da plataforma num lugar só" }, []);
  const { grupos, reordenarGrupos, reordenarItens, restaurarPadrao, personalizado } = useOrdemDoMenu(
    useGruposVisiveis(),
  );
  const [reordenando, setReordenando] = useState(false);
  const isMobile = useIsMobile();
  const itens = grupos
    .flatMap((g) => g.items.map((item) => ({ ...item, grupo: g.label })))
    .filter((i) => i.href !== "/menu");
  // Sem títulos de seção, clínica + laboratório viravam dois "Arquivos" iguais: o repetido leva a seção.
  const repetidos = new Set(itens.map((i) => i.label).filter((l, i, todos) => todos.indexOf(l) !== i));

  return (
    <div className="mx-auto w-full max-w-[1400px] pb-10">
      {reordenando ? (
        <div className="rounded-xl border border-zinc-200 bg-white p-2 md:hidden dark:border-border dark:bg-card">
          <MenuReordenavel grupos={grupos} onGrupos={reordenarGrupos} onItens={reordenarItens} />
        </div>
      ) : (
        // Duas colunas no celular, com o cartão compacto: uma coluna de cartões altos obrigava a rolar
        // muito; três colunas em 360px cortariam "Configurações" e "Receituário".
        <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-3">
          {itens.map((item) => (
            <CartaoComDestaque
              key={item.href}
              para={item.href}
              estado={VEIO_DO_MENU}
              cor={item.accent ?? "#2457ff"}
              icone={item.icon}
              titulo={repetidos.has(item.label) && item.grupo ? `${item.label} · ${item.grupo}` : item.label}
              descricao={item.description}
              compacto={isMobile}
              className={isMobile ? undefined : "min-h-[148px]"}
            />
          ))}
        </div>
      )}
      <div className="mt-4 md:hidden">
        <SidebarRodape
          reordenando={reordenando}
          personalizado={personalizado}
          collapsed={false}
          onEntrar={() => setReordenando(true)}
          onConcluir={() => setReordenando(false)}
          onRestaurar={restaurarPadrao}
        />
      </div>
    </div>
  );
}
